import type { TabsPassThroughOptions } from 'primevue/tabs';
import type { TabListPassThroughOptions } from 'primevue/tablist';
import type { TabProps } from 'primevue/tab';
import type {
  TabPanelPassThroughOptions,
  TabPanelProps,
} from 'primevue/tabpanel';
import type { PassThrough } from '@primevue/core';
import type { InjectionKey, Ref } from 'vue';
import type { IconType } from 'src/design_system/components/icon/types';
import type { PassThroughOptionType } from 'src/design_system/components/utils';

export type { TabProps, TabPanelProps };

export type TabTransitionDirection = 'forward' | 'backward' | 'none';

// ---- Injection keys for internal tab communication ----

/** Active tab value, provided by MeridiaTabs */
export const MERIDIA_TABS_ACTIVE_VALUE: InjectionKey<Ref<string | number>> =
  Symbol('meridiaTabsActiveValue');

/** Ordered tab values (mount/registration order), provided by MeridiaTabs via useTabTransition */
export const MERIDIA_TABS_ORDER: InjectionKey<Ref<(string | number)[]>> =
  Symbol('meridiaTabsOrder');

/** Register a tab value for order tracking (transitions), provided by MeridiaTabs */
export const MERIDIA_TABS_REGISTER_TAB: InjectionKey<
  (value: string | number) => void
> = Symbol('meridiaTabsRegisterTab');

/** Unregister a tab value, provided by MeridiaTabs */
export const MERIDIA_TABS_UNREGISTER_TAB: InjectionKey<
  (value: string | number) => void
> = Symbol('meridiaTabsUnregisterTab');

/** Current transition direction, provided by MeridiaTabs via useTabTransition */
export const MERIDIA_TABS_TRANSITION_DIRECTION: InjectionKey<
  Ref<TabTransitionDirection>
> = Symbol('meridiaTabsTransitionDirection');

/** Whether parent MeridiaTabPanels has keepAlive enabled */
export const MERIDIA_TAB_PANELS_KEEP_ALIVE: InjectionKey<Ref<boolean>> = Symbol(
  'meridiaTabPanelsKeepAlive'
);

/** Whether parent MeridiaTabPanels has animation enabled */
export const MERIDIA_TAB_PANELS_ANIMATED: InjectionKey<Ref<boolean>> = Symbol(
  'meridiaTabPanelsAnimated'
);

// ---- Custom PassThrough types ----

export type MeridiaTabPassThroughOptions = {
  /** The root Tab element (PrimeVue Tab root) */
  root?: PassThroughOptionType;
  /** The inner div wrapping icon + label (div.meridia-tab-content) */
  tabContent?: PassThroughOptionType;
  /** The MeridiaIcon element */
  icon?: PassThroughOptionType;
  /** The span.meridia-tab-label element */
  label?: PassThroughOptionType;
};

export type MeridiaTabPanelsPassThroughOptions = {
  /** PrimeVue TabPanels root */
  root?: PassThroughOptionType;
  /** Inner wrapper div (div.meridia-tab-panels-inner) */
  inner?: PassThroughOptionType;
};

export type MeridiaTabPanelContentPassThroughOptions = {
  /** The root div element of the panel content wrapper */
  root?: PassThroughOptionType;
};

// ---- Component props ----

export interface MeridiaTabsProps {
  pt?: PassThrough<TabsPassThroughOptions>;
}

export interface MeridiaTabListProps {
  pt?: PassThrough<TabListPassThroughOptions>;
}
export interface MeridiaTabProps
  extends Pick<TabProps, 'value' | 'disabled' | 'as' | 'asChild'> {
  /** Icon name for MeridiaIcon (e.g. "lab_panel"). When set, icon is rendered before the default slot. */
  icon?: string;
  /** Icon type for MeridiaIcon when using the icon prop. Defaults to "material-symbols-outlined". */
  iconType?: IconType;
  pt?: PassThrough<MeridiaTabPassThroughOptions>;
}

export interface MeridiaTabPanelsProps {
  /** When true, panel content is wrapped in a transition for switch animation. */
  animated?: boolean;
  /** When true, inactive panels are kept in DOM (Vue KeepAlive) to preserve state. */
  keepAlive?: boolean;
  pt?: PassThrough<MeridiaTabPanelsPassThroughOptions>;
}

export interface MeridiaTabPanelProps
  extends Pick<TabPanelProps, 'value' | 'as' | 'asChild'> {
  /** Override the parent MeridiaTabPanels keepAlive setting for this specific panel. */
  keepAlive?: boolean | null;
  pt?: PassThrough<TabPanelPassThroughOptions>;
}

export interface MeridiaTabPanelContentProps {
  pt?: PassThrough<MeridiaTabPanelContentPassThroughOptions>;
}
